import Navigation from "@/components/Navigation";
import Breadcrumbs from "@/components/Breadcrumbs";
import Footer from "@/components/Footer";
import { useSEO } from "@/hooks/useSEO";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Mail, Phone, MapPin } from "lucide-react";
import { useState } from "react";
import { useHubspotForm } from "@/hooks/useHubspotForm";
import { trackFormSubmit } from "@/lib/analytics";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { contactFormSchema, type ContactFormData } from "@/lib/validations/contactForm";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";

const Contact = () => {
  useSEO({
    title: 'Contact Ommni - Get in Touch with Our Team',
    description: 'Contact Ommni to learn more about our commerce operations platform. Email, phone, or live chat support available. Request a demo or speak with a solutions expert.',
    keywords: 'contact ommni, request demo, sales inquiry, customer support, commerce platform demo',
    canonical: 'https://ommni.io/contact',
  });

  const { submitToHubspot, isSubmitting } = useHubspotForm("08948fc0-3931-4bd9-b895-46302eb6e605");
  const [selectedCountry, setSelectedCountry] = useState("us");

  const phoneNumbers = {
    us: { number: "+1 512-200-4779", display: "+1 512-200-4779", label: "United States" },
    au: { number: "+61 4 0076 9964", display: "+61 4 0076 9964", label: "Australia" }
  };

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      firstname: "",
      lastname: "",
      email: "",
      company: "",
      phone: "",
      message: "",
    },
  });

  const onSubmit = async (data: ContactFormData) => {
    const success = await submitToHubspot({
      email: data.email,
      firstname: data.firstname,
      lastname: data.lastname,
      company: data.company || "",
      phone: data.phone || "",
      message: data.message,
      hs_lead_status: "NEW",
    });

    trackFormSubmit('contact_form', success);

    if (success) {
      form.reset();
    }
  };

  return (
    <div className="min-h-screen">
      <Navigation />
      <main className="pt-32 px-4 pb-24">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Let's Talk Commerce
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Ready to transform your commerce operations? Get in touch with our team.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Contact Form */}
            <Card className="p-8 gradient-card">
              <h2 className="text-2xl font-bold mb-6">Send us a message</h2>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="firstname"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>First Name</FormLabel>
                          <FormControl>
                            <Input placeholder="John" {...field} className="mt-2" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lastname"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Last Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Doe" {...field} className="mt-2" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Address</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="john@company.com" {...field} className="mt-2" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company Name (Optional)</FormLabel>
                        <FormControl>
                          <Input placeholder="Your Company" {...field} className="mt-2" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone (Optional)</FormLabel>
                        <FormControl>
                          <Input type="tel" placeholder="+1 234 567 8900" {...field} className="mt-2" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Message</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Tell us about your needs..."
                            className="mt-2 min-h-[150px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button 
                    type="submit" 
                    variant="cta" 
                    size="lg" 
                    className="w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </Form>
            </Card>

            {/* Contact Info */}
            <div className="space-y-6">
              <Card className="p-8 gradient-card">
                <div className="flex items-start space-x-4">
                  <div className="p-3 rounded-lg bg-primary/10">
                    <Mail className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Email Us</h3>
                    <p className="text-muted-foreground">hello@ommni.io</p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 gradient-card">
                <div className="flex items-start space-x-4">
                  <div className="p-3 rounded-lg bg-primary/10">
                    <Phone className="h-6 w-6 text-primary" />
                  </div>
                  <div className="w-full">
                    <h3 className="font-semibold mb-3">Call Us</h3>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label htmlFor="country-select-contact" className="text-sm font-medium">Select Country</Label>
                        <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                          <SelectTrigger id="country-select-contact" className="w-full bg-background">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-background z-50">
                            <SelectItem value="us">{phoneNumbers.us.label}</SelectItem>
                            <SelectItem value="au">{phoneNumbers.au.label}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="text-muted-foreground font-semibold">{phoneNumbers[selectedCountry as keyof typeof phoneNumbers].display}</p>
                      <p className="text-sm text-muted-foreground">
                        Mon-Fri, 9am-6pm EST
                      </p>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="p-8 gradient-card">
                <div className="flex items-start space-x-4">
                  <div className="p-3 rounded-lg bg-primary/10">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Visit Us</h3>
                    <p className="text-muted-foreground">
                      Suite AU1677, 4291 Express Lane
                      <br />
                      Sarasota, FL 34249
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 gradient-hero">
                <h3 className="text-xl font-bold mb-4 text-white">Enterprise Support</h3>
                <p className="text-white/80 mb-6">
                  Need dedicated support or have specific requirements? Our enterprise team is here to help.
                </p>
                <Button variant="secondary" className="w-full bg-white text-foreground hover:bg-white/90" asChild>
                  <a href="mailto:sales@ommni.io">Contact Enterprise Sales</a>
                </Button>
              </Card>
            </div>
          </div>
        </div>
      </main>
      <Breadcrumbs />
      <Footer />
    </div>
  );
};

export default Contact;
